import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Building, Users, Clock, Award, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

const CorporateDiscounts = () => {
  const examPackages = [
    {
      id: 'cire',
      title: 'CIRE',
      fullName: 'Canadian Investment Regulatory Exam',
      color: '#1e40af',
      lightColor: '#dbeafe',
      items: [
        { name: 'Online Video Course', desc: 'Lifetime access — Video tutorials, question bank & mock exams' },
        { name: 'Online Interactive Quick Study Guide', desc: 'Lifetime access — Interactive digital study resource' }
      ],
      total: '$110.00 CAD',
      payLink: 'https://vittafinance.mykajabi.com/offers/nUSRwRWz',
      accessLink: '/corporate/cire-access'
    },
    {
      id: 'rse',
      title: 'RSE',
      fullName: 'Retail Securities Exam',
      color: '#166534',
      lightColor: '#dcfce7',
      items: [
        { name: 'Online Video Course', desc: 'Lifetime access — Video tutorials, question bank & mock exams' },
        { name: 'Online Interactive Quick Study Guide', desc: 'Lifetime access — Interactive digital study resource' }
      ],
      total: '$125.00 CAD',
      payLink: 'https://vittafinance.mykajabi.com/offers/JiaLfkkE',
      accessLink: '/corporate/rse-access'
    },
    {
      id: 'ise',
      title: 'ISE',
      fullName: 'Institutional Securities Exam',
      color: '#7c3aed',
      lightColor: '#ede9fe',
      items: [
        { name: 'Online Video Course', desc: 'Lifetime access — Video tutorials, question bank & mock exams' },
        { name: 'Online Interactive Quick Study Guide', desc: 'Lifetime access — Interactive digital study resource' }
      ],
      total: '$125.00 CAD',
      payLink: 'https://vittafinance.mykajabi.com/offers/qS4cREb7',
      accessLink: '/corporate/ise-access'
    },
    {
      id: 'derivatives',
      title: 'Derivatives',
      fullName: 'CIRO Derivative Exam',
      color: '#92400e',
      lightColor: '#fef3c7',
      items: [
        { name: 'Online Video Course', desc: 'Lifetime access — Video tutorials, question bank & mock exams' },
        { name: 'Online Interactive Quick Study Guide', desc: 'Lifetime access — Interactive digital study resource' }
      ],
      total: '$125.00 CAD',
      payLink: 'https://vittafinance.mykajabi.com/offers/eoHHKFZN',
      accessLink: '/corporate/derivatives-access'
    },
    {
      id: 'traders',
      title: 'Traders',
      fullName: 'CIRO Traders Licensing Exam',
      color: '#0d9488',
      lightColor: '#ccfbf1',
      items: [
        { name: 'Online Video Course', desc: 'Lifetime access — Video lectures, podcast, games, question bank & mock exams' },
        { name: 'Online Study Book', desc: 'Lifetime access — Comprehensive digital study resource' }
      ],
      total: '$175.00 CAD',
      payLink: null,
      accessLink: '/traders-exam',
      comingSoon: true
    }
  ];

  const keyTerms = [
    { icon: CheckCircle, text: 'Unlimited coupon codes — no cap on candidates' },
    { icon: CheckCircle, text: 'No monthly payments — one-time flat fee per candidate' },
    { icon: CheckCircle, text: 'Valid until December 31, 2027' }
  ];

  return (
    <>
      <SEO 
        title="Corporate Discounts | CIRO Exam Prep for Firms | Vitta Finance Academy"
        description="Special corporate pricing for CIRO exam preparation. Unlimited coupon codes, no monthly payments, lifetime access for your team."
        keywords="corporate CIRO training, firm exam prep, bulk CIRE training, corporate RSE training, institutional exam preparation"
      />

      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)',
        padding: '80px 20px',
        paddingTop: '100px',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(212, 175, 55, 0.2)',
            border: '1px solid #d4af37',
            borderRadius: '20px',
            padding: '6px 16px',
            marginBottom: '20px'
          }}>
            <Building size={16} color="#d4af37" />
            <span style={{ color: '#d4af37', fontSize: '13px', fontWeight: '600' }}>FOR FIRMS & INSTITUTIONS</span>
          </div>
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: '700',
            marginBottom: '16px',
            fontFamily: 'Georgia, serif'
          }}>
            Corporate CIRO Exam Prep
          </h1>
          <p style={{
            fontSize: '1.2rem',
            opacity: '0.9',
            maxWidth: '600px',
            margin: '0 auto 30px',
            lineHeight: '1.6'
          }}>
            Special pricing for firms. One-time payment per candidate with lifetime access to all study materials. Does not include printed book.
          </p>
          
          {/* Key Terms */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            justifyContent: 'center',
            marginTop: '30px'
          }}>
            {keyTerms.map((term, idx) => (
              <div key={idx} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(255,255,255,0.1)',
                padding: '10px 18px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                <term.icon size={18} color="#22c55e" />
                <span>{term.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section style={{ padding: '60px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>

          {examPackages.map((pkg) => (
            <div key={pkg.id} style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '32px',
              marginBottom: '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              position: 'relative'
            }}>
              {pkg.comingSoon && (
                <div style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '0.7rem',
                  fontWeight: '700'
                }}>COMING SOON</div>
              )}
              <h2 style={{
                fontFamily: 'Georgia, serif',
                fontSize: '1.35rem',
                fontWeight: '700',
                color: '#0f172a',
                marginBottom: '24px',
                paddingBottom: '16px',
                borderBottom: `2px solid ${pkg.color}`
              }}>
                {pkg.title} — {pkg.fullName}
              </h2>

              {pkg.items.map((item, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  padding: '12px 0',
                  borderBottom: idx < pkg.items.length - 1 ? '1px dashed #e2e8f0' : 'none'
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: pkg.color,
                    marginTop: '6px',
                    marginRight: '12px',
                    flexShrink: 0
                  }} />
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '15px', color: '#0f172a' }}>{item.name}</div>
                    <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>{item.desc}</div>
                  </div>
                </div>
              ))}

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '20px',
                padding: '18px 20px',
                borderRadius: '10px',
                background: pkg.lightColor
              }}>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '15px', color: '#0f172a' }}>Total per Candidate</div>
                  <div style={{ fontSize: '12px', color: '#475569', marginTop: '2px' }}>Lifetime access — one-time payment</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {!pkg.comingSoon && (
                    <div style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#166534',
                      background: '#dcfce7',
                      padding: '4px 10px',
                      borderRadius: '4px'
                    }}>
                      10% OFF
                    </div>
                  )}
                  <div style={{
                    fontFamily: 'Georgia, serif',
                    fontWeight: '700',
                    fontSize: '1.5rem',
                    color: '#0f172a'
                  }}>
                    {pkg.total}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '16px',
                marginTop: '24px',
                flexWrap: 'wrap'
              }}>
                {pkg.comingSoon ? (
                  <div
                    style={{
                      flex: 1,
                      minWidth: '200px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '14px 24px',
                      background: '#e2e8f0',
                      color: '#64748b',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      fontWeight: '600'
                    }}
                  >
                    Coming Soon
                  </div>
                ) : (
                  <a
                    href={pkg.payLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`${pkg.id}-pay-now-btn`}
                    style={{
                      flex: 1,
                      minWidth: '200px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '14px 24px',
                      background: pkg.color,
                      color: 'white',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      fontWeight: '600',
                      textDecoration: 'none',
                      transition: 'opacity 0.2s'
                    }}
                  >
                    Pay Now
                    <ArrowRight size={18} />
                  </a>
                )}
                <Link
                  to={pkg.accessLink}
                  data-testid={`${pkg.id}-access-btn`}
                  style={{
                    flex: 1,
                    minWidth: '200px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '14px 24px',
                    background: 'white',
                    color: pkg.color,
                    border: `2px solid ${pkg.color}`,
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    textDecoration: 'none',
                    transition: 'all 0.2s'
                  }}
                >
                  After Payment Access
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer Note */}
      <section style={{ padding: '40px 20px', background: 'white', textAlign: 'center' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <p style={{ fontSize: '14px', color: '#64748b' }}>
            <strong style={{ color: '#0f172a' }}>Vitta Finance Academy</strong> | vittafinance.ca<br />
            All prices in Canadian Dollars (CAD) | Offer valid until December 31, 2027
          </p>
          <Link
            to="/institutional"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              marginTop: '20px',
              color: '#1e40af',
              fontSize: '14px',
              fontWeight: '600',
              textDecoration: 'none'
            }}
          >
            Back to Institutional Solutions
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </>
  );
};

export default CorporateDiscounts;
